import React from 'react'
import {NullAsDefault} from 'views/Shared'
import _ from "lodash";
import {SacramentItem} from "../components/CatholicHouseholdComponents";
import Toggle from "../../../../components/Toggle";
import {renderIf, renderIfElse} from "../../../Shared";
import EmptyHolder from "../../../../components/EmptyHolder";
import {Avatar} from "antd";
import {RiUser2Fill} from "react-icons/ri";
import CRUD from "../../../Shared/enums/CRUD";
import {GiIronCross, GiPlainCircle} from "react-icons/gi";
import classNames from "classnames";
import {CatholicHouseholdStatus} from "../../../Workspace/CatholicHousehold/functions/renderCatholicHousehold";
import {Link} from "react-router-dom";
import getLinkToDetail from "../../../../helpers/getLinkToDetail";
import getTitle from "../../../../helpers/getTitle";
import getAliasName from "../../../../helpers/getAliasName";
import {formatDate} from "../../../../helpers/datetimeHelpers";

const roleStyleMap = {
    FATHER: 'text-blue-600 bg-blue-50',
    MOTHER: 'text-pink-500 bg-pink-50',
    CHILDREN: 'text-green-600 bg-green-50',
    OTHER: 'text-color-400 bg-color-50',
}

const roleLabelMap = {
    FATHER: 'father',
    MOTHER: 'mother',
    CHILDREN: 'children',
    OTHER: 'other member',
}

export const renderCatholicHouseholdMember = ({translate: t, isLg, renderContextMenu = NullAsDefault, handleAction = NullAsDefault} = {}) => (item, index) => {

    const { patron_saint_name, name, birth_province, birth_date, sacraments = [], member_user, status, others = [], relationship_type } = item ?? {}

    const current_status = _.find(CatholicHouseholdStatus, {value: status}) ?? {}
    const roleLabel = roleLabelMap[relationship_type]
    const roleStyle = roleStyleMap[relationship_type]

    return (
        <div key={index} className="p-3 flex gap-3 custom-item-render relative list-item">
            <Avatar
                size={40}
                src={member_user?.avatar}
                icon={<RiUser2Fill size={20} className="text-color-500"/>}
                className="flex flex-center background-200 flex-shrink-0"
            />
            <div className="flex flex-col flex-1 min-w-0 gap-1.5">
                {/* Name row + role badge + status */}
                <div className="flex items-start justify-between gap-2">
                    <div
                        onClick={() => {handleAction(CRUD.READ, item)}}
                        className="font-bold text-color-000 hover:text-primary cursor-pointer leading-snug flex-1">
                        <span>
                            {getAliasName(item, 'patron_saint') || ''}
                            {` ${name} `}
                        </span>

                        {renderIf(
                            roleLabel,
                            <span className={classNames("text-2xs font-semibold px-1.5 py-0.5 rounded", roleStyle)}>
                                {t(roleLabel)}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0 mr-5">
                        {renderIf(
                            status !== 1,
                            <span className={classNames("font-medium text-2xs italic", current_status?.className)}>
                                {t(current_status?.label)}
                            </span>
                        )}
                    </div>
                </div>

                {/* Birth date row — always visible, shows --- when empty */}
                <div className="flex items-center gap-2 text-xs">
                    <span className="text-color-400 font-medium">{t('date of birth')}:</span>
                    <span className={classNames(
                        birth_date ? 'text-color-200 font-medium' : 'text-color-500 italic'
                    )}>
                        {birth_date ? formatDate(birth_date, 'DD/MM/YYYY') : '---'}
                    </span>
                    {renderIf(
                        birth_province,
                        <span className="text-color-400">
                            {'· '}{birth_province?.location_name}
                        </span>
                    )}
                </div>

                {/* Sacraments toggle — always show header */}
                <Toggle>
                    {(isToggle, toggle) => (
                        <div className="flex flex-col">
                            <div className="mt-0.5 flex items-center gap-3">
                                <div className="flex items-center gap-1">
                                    <GiIronCross size={9} className="text-color-400 mr-0.5"/>
                                    <span className="text-2xs text-color-400 uppercase tracking-wide font-medium">
                                        {t('sacraments received')}
                                    </span>
                                    <span style={{fontSize: 11}} className={classNames(
                                        "font-bold leading-snug ml-0.5",
                                        sacraments.length > 0 ? 'text-blue-600' : 'text-color-500'
                                    )}>
                                        {`(${sacraments.length})`}
                                    </span>
                                </div>
                                {sacraments.length > 0 && (
                                    <div
                                        onClick={() => {toggle()}}
                                        className="text-xs text-color-500 hover:text-color-100 italic cursor-pointer">
                                        {isToggle ? `...${t('see less')}` : `${t('see more')}...`}
                                    </div>
                                )}
                            </div>
                            {isToggle && renderIfElse(
                                sacraments.length > 0,
                                <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-x-2 md:gap-x-3 gap-y-1">
                                    {sacraments.map((e, i) => (
                                        <SacramentItem key={i} item={e}/>
                                    ))}
                                </div>,
                                <EmptyHolder/>
                            )}
                        </div>
                    )}
                </Toggle>

                {/* Other households — only show when present */}
                {others.length > 0 && (
                    <Toggle>
                        {(isToggle, toggle) => (
                            <div className="flex flex-col mt-0.5">
                                <div
                                    onClick={() => {toggle()}}
                                    className="flex items-center gap-2">
                                    <div className="flex items-center flex-1 gap-1 font-medium">
                                        <GiIronCross size={9} className="text-color-400"/>
                                        <span className="text-2xs text-color-400 uppercase tracking-wide leading-tight">
                                            {t('catholic household other')}
                                        </span>
                                        <span style={{fontSize: 10}} className="font-bold text-primary-400 leading-snug ml-0.5">
                                            {`(${others.length})`}
                                        </span>
                                    </div>
                                    <span className="text-xs text-color-500 hover:text-color-100 italic tracking-wide cursor-pointer">
                                        {isToggle ? `...${t('see less')}` : `${t('see more')}...`}
                                    </span>
                                </div>
                                {isToggle && (
                                    <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-1">
                                        {others.map((eee, iii) => (
                                            <div key={iii} className="flex items-center gap-2">
                                                <GiPlainCircle size={9} className="text-primary-100"/>
                                                <Link
                                                    target="_blank"
                                                    to={getLinkToDetail(eee)}
                                                    className="font-semibold text-sm text-primary-500 italic no-underline">
                                                    {`${t('catholic household')} ${getTitle(eee)}`}
                                                </Link>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </Toggle>
                )}
            </div>
            {renderIf(
                !!item?.edit && _.isFunction(renderContextMenu),
                <div className="absolute right-8 top-8 contextIcon">
                    {renderContextMenu(item)}
                </div>
            )}
        </div>
    )
}
